import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import useApi from "../CustomHooks/useApi";
import axios from "axios";

const Lienamount = () => {

  const Hurl = process.env.REACT_APP_Helpdesk_URL

  const [userData, setUserData] = useState([]);
  const [mobileNumber, setMobileNumber] = useState("");
  const [loading, setLoading] = useState(false);

  // Separate loading states for each button
  const [loadingStates, setLoadingStates] = useState({
    ADD: false,
    SUB: false,
    UNHOLD: false,
    AEPS_TOGGLE: false
  });

  const [lienAmount, setLienAmount] = useState("");
  const [reason, setReason] = useState(""); // New reason state
  const [notification, setNotification] = useState(null);

  // New states for service permissions
  const [services, setServices] = useState([
    { 
      name: 'AEPS', 
      serviceKey: 'aeps', 
      isActive: false 
    }
  ]);
  const [serviceLoading, setServiceLoading] = useState(true);
  const [serviceError, setServiceError] = useState(null);

  const userId = useSelector((state) => state.auth.employeeDetails.userId);
  const token = useSelector((state) => state.auth.employeeDetails?.token);
  const username = useSelector((state) => state.auth.employeeDetails?.username);

  const { callApi: fetchAccount } = useApi(
    `/itpay/dmt/wallet/get/merchant`,
    "GET"
  );

  const { callApi: lienamount } = useApi(
    `/itpay/dmt/wallet/initiate/lien-amount`,
    "POST"
  );

  // Fetch service permissions on component mount and update merchant data
  useEffect(() => {
    const fetchServicePermissions = async () => {
      if (!userId || !token) {
        setServiceLoading(false);
        return;
      }
      
      try {
        const res = await axios.get(`${Hurl}/api/service-status?user_id=${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // Extract serviceStatus from the response
        const serviceStatus = res.data.serviceStatus;
        
        // Create a mapping object for easier access
        const serviceMapping = {
          'aeps': serviceStatus.isAepsEnabled,
          'dmt': serviceStatus.isDmtEnabled,
          'ppi': serviceStatus.isPPIEnabled,
          'ccbp': serviceStatus.isCcbpEnabled,
          'pgin': serviceStatus.isPgInEnabled,
          'payout': serviceStatus.isPayoutEnabled,
          'recharge': serviceStatus.isRechargeEnabled
        };
        
        // Update services with API response
        setServices(prevServices =>
          prevServices.map(service => ({
            ...service,
            isActive: serviceMapping[service.serviceKey] || false
          }))
        );
        
        // Update merchant data with current AEPS status from service API
        if (userData.length > 0 && serviceStatus.isAepsEnabled !== undefined) {
          setUserData(prevData =>
            prevData.map(merchant => ({
              ...merchant,
              isAepsEnabled: serviceStatus.isAepsEnabled
            }))
          );
        }
        
        setServiceError(null);
      } catch (error) {
        console.error('Error fetching service permissions:', error);
        setServiceError('Failed to load service permissions');
        
        // Set all services to inactive on error
        setServices(prevServices =>
          prevServices.map(service => ({
            ...service,
            isActive: false
          }))
        );
      } finally {
        setServiceLoading(false);
      }
    };
    
    fetchServicePermissions();
  }, [userId, token, Hurl, userData.length]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Mobile number validation function
  const validateMobileNumber = (mobile) => {
    // Remove any non-digit characters
    const cleanMobile = mobile.replace(/\D/g, '');
    
    // Check if length is between 6-10 digits
    if (cleanMobile.length < 6 || cleanMobile.length > 10) {
      return { isValid: false, message: "Mobile number must be between 6-10 digits" };
    }
    
    // Check if first digit is between 6-9
    const firstDigit = parseInt(cleanMobile.charAt(0));
    if (firstDigit < 6 || firstDigit > 9) {
      return { isValid: false, message: "Mobile number must start with digits 6-9" };
    }
    
    return { isValid: true, message: "" };
  };

  // Amount validation function
  const validateAmount = (amount) => {
    // Check if it's a valid number and positive
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      return { isValid: false, message: "Please enter a valid positive amount" };
    }
    return { isValid: true, message: "" };
  };

  // Handle mobile number input change
  const handleMobileNumberChange = (e) => {
    const value = e.target.value;
    // Only allow digits and limit to 10 characters
    const cleanValue = value.replace(/\D/g, '').slice(0, 10);
    setMobileNumber(cleanValue);
  };

  // Handle amount input change
  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      return;
    }
    
    setLienAmount(cleanValue);
  };

  // Helper function to set loading state for specific button
  const setButtonLoading = (mode, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [mode]: isLoading
    }));
  };

  const fetchData = async (mobile) => {
    if (!mobile.trim()) {
      showNotification("Please enter a mobile number", 'error');
      return;
    }

    // Validate mobile number before API call
    const mobileValidation = validateMobileNumber(mobile);
    if (!mobileValidation.isValid) {
      showNotification(mobileValidation.message, 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetchAccount(null, `?mobilenumber=${mobile}`);
      console.log("API Response:", response);

      // Check if response exists and has data
      if (response) {
        let merchantData = [];
        
        // If it's an array with data
        if (Array.isArray(response) && response.length > 0) {
          merchantData = response;
        }
        // If it's an object with an array property (check common properties)
        else if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          merchantData = response.data;
        }
        // If it's a single object, convert to array
        else if (response.mid || response.beneficiaryAccountNumber) {
          merchantData = [response];
        }
        
        if (merchantData.length > 0) {
          // Fetch current service status and update merchant data
          try {
            const serviceRes = await axios.get(`${Hurl}/api/service-status?user_id=${userId}`, {
              headers: {
                Authorization: `Bearer ${token}`
              }
            });
            
            const serviceStatus = serviceRes.data.serviceStatus;
            
            // Update merchant data with current AEPS status from service API
            const updatedMerchantData = merchantData.map(merchant => ({
              ...merchant,
              isAepsEnabled: serviceStatus.isAepsEnabled
            }));
            
            setUserData(updatedMerchantData);
          } catch (serviceError) {
            console.error("Failed to fetch service status:", serviceError);
            // Use original merchant data if service status fetch fails
            setUserData(merchantData);
          }
          
          showNotification("Merchant data found successfully!", 'success');
        } else {
          setUserData([]);
          showNotification("No data found for this mobile number", 'error');
        }
      } else {
        setUserData([]);
        showNotification("No data found for this mobile number", 'error');
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      setUserData([]);
      showNotification("Error fetching data. Please try again.", 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchData(mobileNumber);
  };

const handleLienAmount = async (mode, amount = null) => {
  if (!mobileNumber.trim()) {
    showNotification("Please search for a merchant first", 'error');
    return;
  }

  // Validation for mobile number
  const mobileValidation = validateMobileNumber(mobileNumber);
  if (!mobileValidation.isValid) {
    showNotification(mobileValidation.message, 'error');
    return;
  }

  // Validation for ADD and SUB modes
  if ((mode === "ADD" || mode === "SUB") && !lienAmount.trim()) {
    showNotification("Please enter an amount", 'error');
    return;
  }

  // Validate amount for ADD and SUB modes
  if ((mode === "ADD" || mode === "SUB")) {
    const amountValidation = validateAmount(lienAmount);
    if (!amountValidation.isValid) {
      showNotification(amountValidation.message, 'error');
      return;
    }
  }

  // Validation for reason field - required for all operations
  if (!reason.trim()) {
    showNotification("Please enter a reason for this operation", 'error');
    return;
  }

  // Set loading for specific button only
  setButtonLoading(mode, true);

  try {
    const body = {
      "mobile_number": mobileNumber,
      "lien_amount": amount !== null ? amount : lienAmount,
      "modified_by": userId,
      "mode": mode,
      "reason": reason.trim() // Add reason to API payload
    };

    console.log("Lien Amount Request Body:", body);
    const response = await lienamount(body);
    console.log("Lien Amount Response:", response);
    if (response) {
      if (response.status || response.mid) {
        const responseStatus = response.status;
        const apiMessage = response.message;
        const holdAmount = response.holdAmount;
        const lienId = response.lienId;
        
        let message = "";
        if (apiMessage) {
          message = `✅ ${apiMessage}\n• Hold Amount: ₹${holdAmount || 'N/A'}\n• Lien ID: ${lienId || 'N/A'}\n• Status: ${responseStatus || 'N/A'}`;
        } else {
          if (responseStatus === 'HOLD') {
            message = `✅ Amount Successfully Held!\n• Amount: ₹${holdAmount || lienAmount}\n• Lien ID: ${lienId}\n• Status: ${responseStatus}`;
          } else if (responseStatus === 'UNHOLD') {
            message = `✅ Amount Successfully Released!\n• Amount: ₹${holdAmount || 0}\n• Lien ID: ${lienId}\n• Status: ${responseStatus}`;
          } else {
            switch (mode) {
              case "ADD":
                message = `✅ Successfully held amount ₹${holdAmount || lienAmount}`;
                break;
              case "SUB":
                message = `✅ Successfully released amount ₹${holdAmount || lienAmount}`;
                break;
              case "UNHOLD":
                message = `✅ Successfully cleared all held amounts`;
                break;
              default:
                message = "✅ Operation completed successfully";
            }
          }
        }
        showNotification(message, 'success');
        setLienAmount("");
        setReason(""); 
        
      } else {
        showNotification("❌ Operation failed. Invalid response from server.", 'error');
      }
    } else {
      showNotification("❌ Operation failed. No response from server.", 'error');
    }
  } catch (error) {
    console.error("Failed to process lien amount:", error);
    showNotification(`❌ Error: ${error.message || 'Failed to process request. Please try again.'}`, 'error');
  } finally {
    setButtonLoading(mode, false);
  }
};

  const handleAepsToggle = async (currentStatus) => {
    if (!mobileNumber.trim()) {
      showNotification("Please search for a merchant first", 'error');
      return;
    }

    const mobileValidation = validateMobileNumber(mobileNumber);
    if (!mobileValidation.isValid) {
      showNotification(mobileValidation.message, 'error');
      return;
    }

    // Check if user has permission to toggle AEPS
    const aepsService = services.find(service => service.serviceKey === 'aeps');
    if (!aepsService || !aepsService.isActive) {
      showNotification("❌ You don't have permission to toggle AEPS service", 'error');
      return;
    }

    setButtonLoading('AEPS_TOGGLE', true);

    try {
      const body = {
        "mobile_number": mobileNumber,
        "serviceKey": "isAepsEnabled",
        "newValue": !currentStatus,
        "updatedBy": username
      };

      console.log("AEPS Toggle Request Body:", body);

      const response = await axios.put(`${Hurl}/api/toggle-service`, body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log("AEPS Toggle Response:", response.data);

      if (response.status === 200 || response.data) {
        const newStatus = !currentStatus ? 'Enabled' : 'Disabled';
        showNotification(`✅ AEPS Successfully ${newStatus}!`, 'success');
        setUserData(prevData =>
          prevData.map(merchant => ({
            ...merchant,
            isAepsEnabled: !currentStatus
          }))
        );
      } else {
        showNotification("❌ Failed to toggle AEPS status", 'error');
      }
    } catch (error) {
      console.error("Failed to toggle AEPS:", error);
      showNotification(`❌ Error: ${error.response?.data?.message || error.message || 'Failed to toggle AEPS. Please try again.'}`, 'error');
    } finally {
      setButtonLoading('AEPS_TOGGLE', false);
    }
  };

  const getMobileValidationStatus = () => {
    if (!mobileNumber) return { isValid: true, message: "" };
    return validateMobileNumber(mobileNumber);
  };

  const getAmountValidationStatus = () => {
    if (!lienAmount) return { isValid: true, message: "" };
    return validateAmount(lienAmount);
  };

  const mobileValidationStatus = getMobileValidationStatus();
  const amountValidationStatus = getAmountValidationStatus();

  // Get AEPS service permission status
  const aepsService = services.find(service => service.serviceKey === 'aeps');
  const hasAepsPermission = aepsService ? aepsService.isActive : false;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Lien Amount</h1>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6 border border-gray-200">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">Search by Mobile Number for Merchant</h2>
        <div className="flex gap-4 items-start flex-col sm:flex-row">
          <div className="flex-1">
            <input
              type="text"
              value={mobileNumber}
              onChange={handleMobileNumberChange}
              placeholder="Enter Mobile Number (6-10 digits, starting with 6-9)"
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors ${
                !mobileValidationStatus.isValid && mobileNumber
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-300'
              }`}
              maxLength={10}
            />
            {!mobileValidationStatus.isValid && mobileNumber && (
              <p className="text-red-500 text-xs mt-1">{mobileValidationStatus.message}</p>
            )}
            {mobileNumber && mobileValidationStatus.isValid && (
              <p className="text-green-500 text-xs mt-1">✓ Valid mobile number</p>
            )}
          </div>
          <button
            onClick={handleSearch}
            disabled={loading || !mobileValidationStatus.isValid || !mobileNumber}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Searching...
              </span>
            ) : "Search"}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          • Mobile number must be 6-10 digits long<br/>
          • Must start with digits 6, 7, 8, or 9
        </p>
      </div>
      
      {userData.length > 0 && (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6 border border-gray-200">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Merchant Information</h2>
          {userData.map((merchant, index) => (
            <div key={index} className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 rounded-lg border border-gray-200 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-sm font-medium text-gray-600 mb-1">Merchant ID:</p>
                  <p className="text-lg font-semibold text-gray-800">{merchant.mid}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-sm font-medium text-gray-600 mb-1">User ID:</p>
                  <p className="text-lg font-semibold text-gray-800">{merchant.userId}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-sm font-medium text-gray-600 mb-1">Merchant Name:</p>
                  <p className="text-lg font-semibold text-gray-800">{merchant.merchantName}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-sm font-medium text-gray-600 mb-1">Mobile Number:</p>
                  <p className="text-lg font-semibold text-gray-800">{merchant.mobileNumber}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-sm font-medium text-gray-600 mb-1">Account Number:</p>
                  <p className="text-lg font-semibold text-gray-800">{merchant.merchantAccountNo}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-sm font-medium text-gray-600 mb-1">Role:</p>
                  <p className="text-lg font-semibold text-gray-800">{merchant.role}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-sm font-medium text-gray-600 mb-1">Main Wallet Balance:</p>
                  <p className="text-lg font-semibold text-gray-800">{merchant.mainWalletBalance}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-sm font-medium text-gray-600 mb-1">Aeps Wallet Balance:</p>
                  <p className="text-lg font-semibold text-gray-800">{merchant.aepsWalletBalance}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-sm font-medium text-gray-600 mb-1">Lien Wallet Balance:</p>
                  <p className="text-lg font-semibold text-gray-800">{merchant.lienAmount}</p>
                </div>
                {/* AEPS Status Display with Toggle */}
                <div className="bg-white p-4 rounded-lg shadow-sm col-span-full">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">AEPS Status:</p>
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${merchant.isAepsEnabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                          }`}>
                          {merchant.isAepsEnabled ? '✅ Enabled' : '❌ Disabled'}
                        </span>
                        {/* Service Permission Indicator */}
                        {serviceLoading ? (
                          <div className="animate-pulse">
                            <div className="h-4 bg-gray-300 rounded w-20"></div>
                          </div>
                        ) : (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            hasAepsPermission
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {hasAepsPermission ? '🔑 Can Manage' : '🔒 No Permission'}
                          </span>
                        )}
                      </div>
                      {!hasAepsPermission && !serviceLoading && (
                        <p className="text-xs text-orange-600 mt-1">
                          Contact administrator for AEPS management access
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <button
                        onClick={() => handleAepsToggle(merchant.isAepsEnabled)}
                        disabled={loadingStates.AEPS_TOGGLE || serviceLoading}
                        className={`px-4 py-2 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ${
                          !hasAepsPermission
                            ? 'bg-gray-400 cursor-not-allowed'
                            : merchant.isAepsEnabled
                              ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                              : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                          }`}
                      >
                        {loadingStates.AEPS_TOGGLE ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                          </span>
                        ) : serviceLoading ? (
                          'Loading...'
                        ) : !hasAepsPermission ? (
                          '🔒 Access Denied'
                        ) : (
                          merchant.isAepsEnabled ? '🔴 Disable AEPS' : '🟢 Enable AEPS'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lien Amount Section */}
      {userData.length > 0 && (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6 border border-gray-200">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Lien Amount Management</h2>

          {/* Amount Input */}
          <div className="mb-6">
            <label htmlFor="lienAmount" className="block text-sm font-medium text-gray-600 mb-2">
              Enter Amount:
            </label>
            <input
              id="lienAmount"
              type="text"
              value={lienAmount}
              onChange={handleAmountChange}
              placeholder="Enter amount (numbers only)"
              className={`w-full max-w-md px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors ${
                !amountValidationStatus.isValid && lienAmount
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-300'
              }`}
            />
            {!amountValidationStatus.isValid && lienAmount && (
              <p className="text-red-500 text-xs mt-1">{amountValidationStatus.message}</p>
            )}
            {lienAmount && amountValidationStatus.isValid && (
              <p className="text-green-500 text-xs mt-1">✓ Valid amount</p>
            )}
          </div>

          {/* Reason Input - Only for Lien Operations */}
          <div className="mb-6">
            <label htmlFor="reason" className="block text-sm font-medium text-gray-600 mb-2">
              Reason for Lien Operation: <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please provide reason for this lien amount operation..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">This field is required for lien amount operations</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => handleLienAmount("ADD")}
              disabled={loadingStates.ADD || !lienAmount.trim() || !reason.trim() || !amountValidationStatus.isValid}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loadingStates.ADD ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : "🔒 Lien Amount"}
            </button>

            <button
              onClick={() => handleLienAmount("SUB")}
              disabled={loadingStates.SUB || !lienAmount.trim() || !reason.trim() || !amountValidationStatus.isValid}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loadingStates.SUB ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : "🔓 UnLien Amount"}
            </button>

            <button
              onClick={() => handleLienAmount("UNHOLD", "0")}
              disabled={loadingStates.UNHOLD || !reason.trim()}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loadingStates.UNHOLD ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : "🧹 Clear All Lien Amount"}
            </button>
          </div>

          {/* Notification Component */}
          {notification && (
            <div className={`mb-6 p-4 rounded-lg border-l-4 ${notification.type === 'success'
                ? 'bg-green-50 border-green-400 text-green-800'
                : 'bg-red-50 border-red-400 text-red-800'
              } relative`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {notification.type === 'success' ? (
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="ml-3 flex-1">
                  <p className="font-medium whitespace-pre-line">{notification.message}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    onClick={() => setNotification(null)}
                    className="inline-flex text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Helper Text */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">Operation Guide:</h3>
            <div className="text-sm text-blue-700 space-y-1">
              <p>• <strong>🟢 Enable AEPS:</strong> Activates AEPS service for the merchant {!hasAepsPermission && <span className="text-red-600">(Requires Permission)</span>}</p>
              <p>• <strong>🔴 Disable AEPS:</strong> Deactivates AEPS service for the merchant {!hasAepsPermission && <span className="text-red-600">(Requires Permission)</span>}</p>
              <p>• <strong>🔒 Lien Amount:</strong> Blocks the specified amount from merchant's wallet (Mode: ADD)</p>
              <p>• <strong>🔓 UnLien Amount:</strong> Releases the specified amount back to merchant's wallet (Mode: SUB)</p>
              <p>• <strong>🧹 Clear All Lien Amount:</strong> Removes all held amounts, sets to 0 (Mode: UNHOLD)</p>
              <p>• <strong>⚠️ Important:</strong> Reason field is mandatory for all lien operations</p>
            </div>
          </div>

          {/* Validation Rules */}
          <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <h3 className="font-semibold text-yellow-800 mb-2">Validation Rules:</h3>
            <div className="text-sm text-yellow-700 space-y-1">
              <p>• <strong>Mobile Number:</strong> Must be 6-10 digits and start with 6, 7, 8, or 9</p>
              <p>• <strong>Amount:</strong> Must be a valid positive number (only digits and decimal allowed)</p>
              <p>• <strong>Reason:</strong> Required for all lien amount operations</p>
              <p>• <strong>AEPS Permission:</strong> Required for enabling/disabling AEPS service</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-300 rounded w-1/3" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-4 bg-gray-200 rounded w-1/4" />
          </div>
        </div>
      )}
    </div>
  );
};

export default Lienamount;